<template>
  <div class="min-h-screen bg-gray-50">
    <div class="max-w-3xl mx-auto px-4 py-10">
      <div
        v-if="loading"
        class="bg-white rounded-lg shadow-sm p-10 text-center text-gray-500"
      >
        <div class="flex justify-center mb-6">
          <img :src="logoUrl" alt="TripDay" class="h-16 sm:h-20 w-auto" />
        </div>
        Carregando…
      </div>

      <div
        v-else-if="loadError"
        class="bg-white rounded-lg shadow-sm p-8 text-center text-red-800"
      >
        <div class="flex justify-center mb-4">
          <img :src="logoUrl" alt="TripDay" class="h-16 sm:h-20 w-auto" />
        </div>
        {{ loadError }}
      </div>

      <template v-else-if="payload">
        <div class="bg-white rounded-lg shadow-sm p-6 mb-4">
          <div class="flex items-center gap-3 mb-4">
            <img :src="logoUrl" alt="TripDay" class="h-16 sm:h-20 w-auto" />
          </div>
          <h1 class="text-2xl font-bold text-gray-900">
            {{ pageTitle }}
          </h1>
          <p v-if="payload.property_name || payload.guest_name" class="mt-2 text-gray-600 text-sm">
            <span v-if="payload.property_name">{{ payload.property_name }}</span>
            <span v-if="payload.property_name && payload.guest_name"> · </span>
            <span v-if="payload.guest_name">{{ payload.guest_name }}</span>
          </p>
          <p v-if="payload.message" class="mt-4 text-gray-700 leading-relaxed text-sm">
            {{ payload.message }}
          </p>
        </div>

        <!-- Rubrica + assinatura + PDF (antes de assinar) -->
        <template v-if="showSignPanel">
          <div class="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-4 space-y-5">
            <h2 class="text-lg font-semibold text-gray-900">
              Rubrica e assinatura
            </h2>
            <p class="text-sm text-gray-600">
              Desenhe no quadro da <strong>rubrica</strong> (será repetida em todas as páginas) e da
              <strong>assinatura</strong> (última página). Use o dedo ou o mouse.
            </p>
            <p
              v-if="signError"
              class="text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg px-3 py-2"
            >
              {{ signError }}
            </p>
            <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <div class="flex items-center justify-between mb-1">
                  <label class="text-sm font-medium text-gray-700">Rubrica</label>
                  <button
                    type="button"
                    class="text-xs text-indigo-600 hover:text-indigo-800"
                    @click="clearRubrica"
                  >
                    Limpar
                  </button>
                </div>
                <canvas
                  ref="rubricaCanvas"
                  class="w-full max-w-md touch-none border-2 border-gray-300 rounded-lg bg-white cursor-crosshair h-36"
                  @pointerdown.prevent="onPointerDown($event, 'rubrica')"
                  @pointermove.prevent="onPointerMove($event, 'rubrica')"
                  @pointerup.prevent="onPointerUp($event, 'rubrica')"
                  @pointerleave.prevent="onPointerUp($event, 'rubrica')"
                />
              </div>
              <div>
                <div class="flex items-center justify-between mb-1">
                  <label class="text-sm font-medium text-gray-700">Assinatura</label>
                  <button
                    type="button"
                    class="text-xs text-indigo-600 hover:text-indigo-800"
                    @click="clearAssinatura"
                  >
                    Limpar
                  </button>
                </div>
                <canvas
                  ref="assinaturaCanvas"
                  class="w-full max-w-md touch-none border-2 border-gray-300 rounded-lg bg-white cursor-crosshair h-36"
                  @pointerdown.prevent="onPointerDown($event, 'assinatura')"
                  @pointermove.prevent="onPointerMove($event, 'assinatura')"
                  @pointerup.prevent="onPointerUp($event, 'assinatura')"
                  @pointerleave.prevent="onPointerUp($event, 'assinatura')"
                />
              </div>
            </div>
            <p
              v-if="!canSubmitContractSign && !signSubmitting"
              class="text-sm text-gray-500"
            >
              Desenhe a <strong>rubrica</strong> e a <strong>assinatura</strong> nos quadros para habilitar a confirmação.
            </p>
            <button
              type="button"
              class="w-full sm:w-auto px-5 py-2.5 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition font-medium text-sm disabled:opacity-50"
              :disabled="signSubmitting || !canSubmitContractSign"
              @click="submitSign"
            >
              {{ signSubmitting ? 'Processando…' : 'Confirmar rubrica e assinar' }}
            </button>
          </div>
          <div class="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
            <iframe
              :key="iframeKey"
              title="Contrato em PDF"
              :src="fullPdfUrl"
              class="w-full border-0 bg-gray-100"
              style="min-height: 80vh;"
            />
          </div>
        </template>

        <!-- Já assinado + identidade confirmada: visualizar / baixar -->
        <template v-else-if="showSignedPdfPanel">
          <div class="space-y-4">
            <div class="bg-emerald-50 border border-emerald-200 rounded-lg p-4 text-emerald-900 text-sm">
              <p class="font-medium">Contrato assinado</p>
              <p class="mt-1 text-emerald-800">
                Você pode visualizar o documento abaixo ou baixar uma cópia.
              </p>
              <a
                :href="downloadPdfHref"
                class="mt-3 inline-flex items-center px-4 py-2 bg-emerald-700 text-white rounded-lg text-sm font-medium hover:bg-emerald-800 transition"
              >
                Baixar PDF assinado
              </a>
            </div>
            <div class="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
              <iframe
                :key="iframeKey"
                title="Contrato assinado em PDF"
                :src="fullPdfUrl"
                class="w-full border-0 bg-gray-100"
                style="min-height: 80vh;"
              />
            </div>
          </div>
        </template>

        <div
          v-else-if="showVerificationForm"
          class="bg-white rounded-lg shadow-sm border border-gray-200 p-6"
        >
          <h2 class="text-lg font-semibold text-gray-900 mb-1">
            Confirme sua identidade
          </h2>
          <p
            v-if="payload.signed"
            class="text-sm text-emerald-800 bg-emerald-50 border border-emerald-200 rounded-lg px-3 py-2 mb-4"
          >
            Este contrato já está assinado. Confirme seus dados para visualizar ou baixar o PDF.
          </p>
          <p class="text-sm text-gray-600 mb-4">
            Para visualizar e assinar o contrato, informe o <strong>CPF/CNPJ</strong> ou o <strong>celular</strong> (com DDD) cadastrados na reserva.
          </p>
          <p
            v-if="verifyError"
            class="text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg px-3 py-2 mb-4"
          >
            {{ verifyError }}
          </p>
          <form class="space-y-4" @submit.prevent="submitVerification">
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-1">CPF/CNPJ</label>
              <input
                v-model.trim="verifyDocument"
                type="text"
                autocomplete="off"
                class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 text-gray-900"
                placeholder="Somente números ou com pontuação"
              />
            </div>
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-1">Celular (com DDD)</label>
              <input
                v-model.trim="verifyPhone"
                type="tel"
                autocomplete="tel"
                class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 text-gray-900"
                placeholder="Ex.: 11 99999-8888"
              />
            </div>
            <button
              type="submit"
              class="w-full sm:w-auto px-5 py-2.5 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition font-medium text-sm disabled:opacity-50"
              :disabled="verifySubmitting"
            >
              {{ verifySubmitting ? 'Verificando…' : (payload.signed ? 'Ver PDF assinado' : 'Ver e abrir contrato') }}
            </button>
          </form>
        </div>

        <div
          v-else-if="payload.signed && !payload.contract_pdf_available"
          class="bg-amber-50 border border-amber-200 rounded-lg p-6 text-amber-900 text-sm text-center"
        >
          O PDF assinado não está disponível para download.
        </div>

        <div
          v-else
          class="bg-amber-50 border border-amber-200 rounded-lg p-6 text-amber-900 text-sm text-center"
        >
          {{ payload.message || 'PDF não disponível neste link.' }}
        </div>
      </template>
    </div>
  </div>
</template>

<script>
export default {
  name: 'PublicContractSign',
  props: {
    token: {
      type: String,
      default: ''
    }
  },
  data() {
    return {
      logoUrl: '/tripday-logo.png',
      loading: true,
      loadError: null,
      payload: null,
      verifiedPdfPath: null,
      pdfAccessSig: null,
      verifyDocument: '',
      verifyPhone: '',
      verifyError: null,
      verifySubmitting: false,
      signError: null,
      signSubmitting: false,
      iframeKey: 0,
      drawState: {
        rubrica: { active: false, ctx: null, dpr: 1, hasInk: false },
        assinatura: { active: false, ctx: null, dpr: 1, hasInk: false }
      }
    }
  },
  computed: {
    pageTitle() {
      const p = this.payload
      if (!p) return 'Contrato'
      return p.signed ? 'Contrato assinado' : 'Contrato para assinatura'
    },

    showVerificationForm() {
      const p = this.payload
      if (!p || !p.contract_pdf_available || !p.verification_required) return false
      return !this.verifiedPdfPath
    },

    showSignPanel() {
      const p = this.payload
      if (!p || p.signed || !p.contract_pdf_available) return false
      return !!this.verifiedPdfPath && !!this.pdfAccessSig
    },

    showSignedPdfPanel() {
      const p = this.payload
      if (!p || !p.signed || !p.contract_pdf_available) return false
      return !!this.verifiedPdfPath && !!this.pdfAccessSig
    },

    canSubmitContractSign() {
      return this.drawState.rubrica.hasInk && this.drawState.assinatura.hasInk
    },

    fullPdfUrl() {
      const rel = this.verifiedPdfPath || this.payload?.contract_pdf_url
      if (!rel) return ''
      if (rel.startsWith('http')) return rel
      return `${window.location.origin}${rel}`
    },

    downloadPdfHref() {
      const t = (this.token || '').trim()
      if (!t) return '#'
      const q = new URLSearchParams({ download: '1' })
      if (this.pdfAccessSig) q.set('sig', this.pdfAccessSig)
      return `${window.location.origin}/api/v1/public/contract_sign/${encodeURIComponent(t)}/pdf?${q.toString()}`
    }
  },
  watch: {
    token: {
      immediate: true,
      handler() {
        this.resetLocalState()
        this.fetchContract()
      }
    },
    showSignPanel(val) {
      if (val) {
        this.$nextTick(() => {
          // Dois frames: o painel/iframe ainda reflowam após um único nextTick;
          // sem isso clientWidth pode estar errado até o usuário clicar em Limpar.
          requestAnimationFrame(() => {
            requestAnimationFrame(() => {
              this.initDrawings()
              this.bindSignCanvasResizeObservers()
            })
          })
        })
      } else {
        this.unbindSignCanvasResizeObservers()
      }
    }
  },
  beforeUnmount() {
    this.unbindSignCanvasResizeObservers()
  },
  methods: {
    resetLocalState() {
      this.verifiedPdfPath = null
      this.pdfAccessSig = null
      this.verifyDocument = ''
      this.verifyPhone = ''
      this.verifyError = null
      this.signError = null
      this.iframeKey = 0
    },

    initDrawings() {
      ;[ 'rubricaCanvas', 'assinaturaCanvas' ].forEach((refName, idx) => {
        const el = this.$refs[refName]
        if (!el || typeof el.getContext !== 'function') return
        const key = idx === 0 ? 'rubrica' : 'assinatura'
        this.setupCanvas(el, key)
      })
    },

    bindSignCanvasResizeObservers() {
      this.unbindSignCanvasResizeObservers()
      if (typeof ResizeObserver === 'undefined') return
      const ro = new ResizeObserver(() => {
        requestAnimationFrame(() => {
          const rub = this.$refs.rubricaCanvas
          const ass = this.$refs.assinaturaCanvas
          if (rub && this.canvasNeedsRelayout(rub)) this.setupCanvas(rub, 'rubrica')
          if (ass && this.canvasNeedsRelayout(ass)) this.setupCanvas(ass, 'assinatura')
        })
      })
      const rub = this.$refs.rubricaCanvas
      const ass = this.$refs.assinaturaCanvas
      if (rub) ro.observe(rub)
      if (ass) ro.observe(ass)
      this._signCanvasResizeObserver = ro
    },

    unbindSignCanvasResizeObservers() {
      if (this._signCanvasResizeObserver) {
        this._signCanvasResizeObserver.disconnect()
        this._signCanvasResizeObserver = null
      }
    },

    canvasNeedsRelayout(canvas) {
      const dpr = window.devicePixelRatio || 1
      const cw = Math.max(1, canvas.clientWidth)
      const ch = Math.max(1, canvas.clientHeight)
      const w = Math.floor(cw * dpr)
      const h = Math.floor(ch * dpr)
      return canvas.width !== w || canvas.height !== h
    },

    setupCanvas(canvas, key) {
      // Use the content box (excludes CSS border) so bitmap size matches pointer coordinates.
      const cw = Math.max(1, canvas.clientWidth)
      const ch = Math.max(1, canvas.clientHeight)
      const dpr = window.devicePixelRatio || 1
      canvas.width = Math.floor(cw * dpr)
      canvas.height = Math.floor(ch * dpr)
      const ctx = canvas.getContext('2d')
      ctx.setTransform(1, 0, 0, 1, 0, 0)
      ctx.scale(dpr, dpr)
      ctx.fillStyle = '#ffffff'
      ctx.fillRect(0, 0, cw, ch)
      ctx.strokeStyle = '#111827'
      ctx.lineWidth = 2
      ctx.lineCap = 'round'
      ctx.lineJoin = 'round'
      this.drawState[key].ctx = ctx
      this.drawState[key].dpr = dpr
      this.drawState[key].hasInk = false
    },

    canvasPoint(canvas, evt) {
      // offsetX/Y are relative to the padding edge and match the drawn bitmap when DPR-scaled.
      if (evt.currentTarget === canvas && typeof evt.offsetX === 'number' && typeof evt.offsetY === 'number') {
        return { x: evt.offsetX, y: evt.offsetY }
      }
      const rect = canvas.getBoundingClientRect()
      const cw = canvas.clientWidth || rect.width
      const ch = canvas.clientHeight || rect.height
      return {
        x: ((evt.clientX - rect.left) / rect.width) * cw,
        y: ((evt.clientY - rect.top) / rect.height) * ch
      }
    },

    onPointerDown(evt, key) {
      const canvas = key === 'rubrica' ? this.$refs.rubricaCanvas : this.$refs.assinaturaCanvas
      if (!canvas) return
      try {
        evt.target.setPointerCapture(evt.pointerId)
      } catch (e) {
        /* ignore */
      }
      const st = this.drawState[key]
      st.active = true
      if (!st.ctx || this.canvasNeedsRelayout(canvas)) this.setupCanvas(canvas, key)
      const p = this.canvasPoint(canvas, evt)
      st.ctx.beginPath()
      st.ctx.moveTo(p.x, p.y)
    },

    onPointerMove(evt, key) {
      const st = this.drawState[key]
      if (!st.active) return
      const canvas = key === 'rubrica' ? this.$refs.rubricaCanvas : this.$refs.assinaturaCanvas
      if (!canvas || !st.ctx) return
      const p = this.canvasPoint(canvas, evt)
      st.ctx.lineTo(p.x, p.y)
      st.ctx.stroke()
      st.hasInk = true
    },

    onPointerUp(evt, key) {
      const st = this.drawState[key]
      st.active = false
      try {
        evt.target.releasePointerCapture(evt.pointerId)
      } catch (e) {
        /* ignore */
      }
    },

    clearRubrica() {
      const canvas = this.$refs.rubricaCanvas
      if (canvas) this.setupCanvas(canvas, 'rubrica')
    },

    clearAssinatura() {
      const canvas = this.$refs.assinaturaCanvas
      if (canvas) this.setupCanvas(canvas, 'assinatura')
    },

    canvasToPngBlob(canvas) {
      return new Promise((resolve) => {
        canvas.toBlob((blob) => resolve(blob), 'image/png', 0.92)
      })
    },

    async fetchContract(options = {}) {
      const silent = options.silent === true
      const t = (this.token || '').trim()
      if (!t) {
        this.loading = false
        this.loadError = 'Link inválido.'
        return
      }

      if (!silent) {
        this.loading = true
        this.loadError = null
        this.payload = null
      }

      try {
        const res = await fetch(`${window.location.origin}/api/v1/public/contract_sign/${encodeURIComponent(t)}`)
        const data = await res.json().catch(() => ({}))

        if (!res.ok) {
          const msg = Array.isArray(data.errors) ? data.errors.join(' ') : (data.errors || 'Não foi possível carregar o contrato.')
          this.loadError = msg
          return
        }

        if (!data.ok) {
          this.loadError = 'Não foi possível carregar o contrato.'
          return
        }

        this.payload = data
      } catch (e) {
        this.loadError = 'Não foi possível carregar o contrato. Verifique sua conexão.'
      } finally {
        if (!silent) {
          this.loading = false
        }
      }
    },

    async submitVerification() {
      const t = (this.token || '').trim()
      this.verifyError = null

      if (!this.verifyDocument && !this.verifyPhone) {
        this.verifyError = 'Informe o CPF/CNPJ ou o celular.'
        return
      }

      this.verifySubmitting = true
      try {
        const res = await fetch(
          `${window.location.origin}/api/v1/public/contract_sign/${encodeURIComponent(t)}/verify`,
          {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({
              document: this.verifyDocument || undefined,
              phone: this.verifyPhone || undefined
            })
          }
        )
        const data = await res.json().catch(() => ({}))

        if (!res.ok) {
          const msg = Array.isArray(data.errors) ? data.errors.join(' ') : (data.errors || 'Não foi possível verificar os dados.')
          this.verifyError = msg
          return
        }

        if (!data.ok || !data.contract_pdf_url) {
          this.verifyError = 'Não foi possível liberar o PDF. Tente novamente.'
          return
        }

        this.verifiedPdfPath = data.contract_pdf_url
        const u = new URL(data.contract_pdf_url, window.location.origin)
        this.pdfAccessSig = u.searchParams.get('sig')
        this.iframeKey += 1
      } catch (e) {
        this.verifyError = 'Erro de rede. Tente novamente.'
      } finally {
        this.verifySubmitting = false
      }
    },

    async submitSign() {
      const t = (this.token || '').trim()
      this.signError = null

      if (!this.pdfAccessSig) {
        this.signError = 'Sessão expirada. Atualize a página e confirme seus dados novamente.'
        return
      }

      if (!this.canSubmitContractSign) {
        this.signError = 'Desenhe a rubrica e a assinatura nos quadros antes de confirmar.'
        return
      }

      const rubCanvas = this.$refs.rubricaCanvas
      const sigCanvas = this.$refs.assinaturaCanvas
      if (!rubCanvas || !sigCanvas) {
        this.signError = 'Quadros de assinatura não carregados.'
        return
      }

      const rubBlob = await this.canvasToPngBlob(rubCanvas)
      const assBlob = await this.canvasToPngBlob(sigCanvas)
      if (!rubBlob || !assBlob || rubBlob.size < 800 || assBlob.size < 800) {
        this.signError = 'Desenhe a rubrica e a assinatura antes de confirmar.'
        return
      }

      this.signSubmitting = true
      try {
        const fd = new FormData()
        fd.append('rubrica', rubBlob, 'rubrica.png')
        fd.append('assinatura', assBlob, 'assinatura.png')

        const q = new URLSearchParams({ sig: this.pdfAccessSig })
        const res = await fetch(
          `${window.location.origin}/api/v1/public/contract_sign/${encodeURIComponent(t)}/sign?${q.toString()}`,
          {
            method: 'POST',
            body: fd
          }
        )
        const data = await res.json().catch(() => ({}))

        if (!res.ok) {
          const msg = Array.isArray(data.errors) ? data.errors.join(' ') : (data.errors || 'Não foi possível assinar o contrato.')
          this.signError = msg
          return
        }

        if (data.contract_pdf_url) {
          this.verifiedPdfPath = data.contract_pdf_url
          const u = new URL(data.contract_pdf_url, window.location.origin)
          this.pdfAccessSig = u.searchParams.get('sig')
        }

        await this.fetchContract({ silent: true })
        this.iframeKey += 1
      } catch (e) {
        this.signError = 'Erro de rede. Tente novamente.'
      } finally {
        this.signSubmitting = false
      }
    }
  }
}
</script>
